import { useState } from 'react';
import { ApiOutlined, CopyOutlined, DownOutlined, FileWordOutlined, PlusOutlined } from '@ant-design/icons';

import { PageHeader } from '../../../components/shared';
import { Button, Dialog, DropdownMenu, Skeleton } from '../../../components/ui';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { GOC_TRO_LY } from '../../../lib/repositories/tro-ly-ai.repository';
import { useToast } from '../../../lib/toast';
import { HopXacNhan } from '../components/chung/HopXacNhan';
import { VungDuLieu } from '../components/chung/VungDuLieu';
import { SoDoNguon } from '../components/nguon/SoDoNguon';
import { SuaKetNoiApi } from '../components/nguon/SuaKetNoiApi';
import { TheNguon } from '../components/nguon/TheNguon';
import { ThemNguon, type LoaiNguonMoi } from '../components/nguon/ThemNguon';
import { QUYEN } from '../constants';
import { useDanhSachNguon } from '../hooks/useChung';
import { useMoTaLoi } from '../hooks/useMoTaLoi';
import {
  useDongBoNguon,
  useDungKho,
  useKiemNguon,
  useLayBangNguon,
  useSuaNguon,
  useXoaKhoCu,
  useXoaNguon,
} from '../hooks/useNguon';
import { GHI_CHU_TOI_DA } from '../nghiep-vu/ghi-chu';
import {
  HD_CHUAN_VAO,
  HD_DSN,
  HD_GRAPHQL,
  TEP_CHUAN_VAO,
  TEP_DSN,
  TEP_GRAPHQL,
} from '../nghiep-vu/huong-dan-ket-noi';
import { dungKhaiNhanh } from '../nghiep-vu/ket-noi';
import { MAU_KIEM_SAP_B1 } from '../nghiep-vu/mau-sap-b1';
import { taiVe, taoDocx } from '../nghiep-vu/tao-docx';
import type { ChanDoanDsn, GraphqlSource } from '../types';

/** Hộp xác nhận đang mở — một ô state thay cho bốn `modal.confirm` của native. */
type XacNhan =
  | { loai: 'xoa'; s: GraphqlSource }
  | { loai: 'xoaCaKho'; s: GraphqlSource }
  | { loai: 'doiCheDo'; s: GraphqlSource }
  | { loai: 'xoaKhoCu'; s: GraphqlSource };

interface KhaiNhanhCho {
  s: GraphqlSource;
  sap: boolean;
  van: string;
  ghiChuMoi: string;
}

/**
 * Kết nối nguồn dữ liệu (`/tro-ly-ai/ket-noi`) — DSN, GraphQL và API, MỘT màn (port `KetNoiView.tsx` native).
 *
 * Vì sao không tách theo loại như kho gốc: dựng kho · dò cấu trúc · khai bảng · đồng bộ là CHUNG cho mọi loại nguồn;
 * tách màn thì màn DSN phải có nút "sang màn kia" — hai màn, một luồng. Chỗ các loại THẬT SỰ khác nhau — biểu mẫu khai
 * — vẫn tách: ba nút "Thêm", ba biểu mẫu.
 */
export function KetNoiView() {
  const { t } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const q = useModulePermissions(QUYEN);

  const [themLoai, setThemLoai] = useState<LoaiNguonMoi | null>(null);
  const [chanDoan, setChanDoan] = useState<Record<string, ChanDoanDsn>>({});
  const [biMat, setBiMat] = useState<{ id: string; secret: string } | null>(null);
  const [soDo, setSoDo] = useState<GraphqlSource | null>(null);
  const [suaApi, setSuaApi] = useState<GraphqlSource | null>(null);
  const [xacNhan, setXacNhan] = useState<XacNhan | null>(null);
  const [khaiNhanh, setKhaiNhanh] = useState<KhaiNhanhCho | null>(null);
  const [dangDoKhai, setDangDoKhai] = useState<string | null>(null);

  const nguon = useDanhSachNguon();
  const kiem = useKiemNguon();
  const dung = useDungKho();
  const dongBo = useDongBoNguon();
  const sua = useSuaNguon();
  const xoa = useXoaNguon();
  const xoaKhoCu = useXoaKhoCu();
  const layBang = useLayBangNguon();

  const ds = nguon.data ?? [];
  const quyenThe = { sua: q.nguonSua, xoa: q.nguonXoa };

  const loiDoi = (e: unknown) => toast.error(t('troLyAiKetNoi.khongDoiDuoc', { loi: moTaLoi(e) }));

  const onKiem = (id: string) =>
    kiem.mutate(id, {
      onSuccess: (d) => {
        if (d.chanDoan) setChanDoan((p) => ({ ...p, [id]: { ...d.chanDoan!, chiThuTuc: d.chiThuTuc, cheDo: d.cheDo } }));
        // Nối được mà thấy 0 bảng là tài khoản thiếu quyền — TRỪ khi nguồn chỉ cho gọi thủ tục, khi ấy đếm thủ tục.
        toast.success(
          d.chiThuTuc
            ? t('troLyAiKetNoi.noiDuocChiThuTuc', { n: d.chanDoan?.soThuTuc ?? 0 })
            : t('troLyAiKetNoi.noiDuoc', { n: d.tables.length }),
        );
      },
      onError: (e) => toast.error(t('troLyAiKetNoi.khongNoiDuoc', { loi: moTaLoi(e) })),
    });

  const onDung = (id: string) =>
    dung.mutate(id, {
      onSuccess: (s) => toast.success(t('troLyAiKetNoi.daDungKho', { ten: s.db.name ?? '' })),
      onError: (e) => toast.error(t('troLyAiKetNoi.khongDungDuocKho', { loi: moTaLoi(e) })),
    });

  const onDongBo = (id: string) =>
    dongBo.mutate(id, {
      onSuccess: (kq) => toast.success(t('troLyAiKetNoi.dongBoBatDau', { n: kq.tables })),
      onError: (e) => toast.error(t('troLyAiKetNoi.khongDongBoDuoc', { loi: moTaLoi(e) })),
    });

  /** Phút ở giao diện, GIÂY ở hợp đồng — đổi đúng một chỗ, tại biên. */
  const doi = (id: string, du: Record<string, unknown>, xong?: string) =>
    sua.mutate({ id, du }, { onSuccess: () => xong && toast.success(xong), onError: loiDoi });

  /**
   * Khai nhanh theo mẫu: dò lược đồ TRƯỚC (nhận ra SAP B1 qua tên bảng), hỏi lại kèm NGUYÊN VĂN thứ sắp ghi, rồi mới
   * ghi. Ghi THÊM vào ghi chú, không đè. Vượt trần thì chặn trước khi gọi máy chủ.
   */
  const batDauKhaiNhanh = async (s: GraphqlSource) => {
    setDangDoKhai(s.id);
    try {
      const bang = (await layBang(s.id)).tables.map((x) => x.table);
      const kq = dungKhaiNhanh(bang, s.ghiChuNghiepVu);
      if (kq.quaDai) {
        toast.error(t('troLyAiKetNoi.khaiNhanhHong', {
          loi: t('troLyAiKetNoi.khaiNhanhQuaDai', { tong: kq.tong, tran: GHI_CHU_TOI_DA }),
        }));
        return;
      }
      setKhaiNhanh({ s, sap: kq.sap, van: kq.van, ghiChuMoi: kq.ghiChuMoi });
    } catch (e) {
      toast.error(t('troLyAiKetNoi.khaiNhanhHong', { loi: moTaLoi(e) }));
    } finally {
      setDangDoKhai(null);
    }
  };

  const ghiKhaiNhanh = () => {
    if (!khaiNhanh) return;
    const { s, sap, ghiChuMoi } = khaiNhanh;
    sua.mutate(
      { id: s.id, du: { ghiChuNghiepVu: ghiChuMoi } },
      {
        onSuccess: () => {
          toast.success(sap ? t('troLyAiKetNoi.daKhaiNhanhSap') : t('troLyAiKetNoi.daKhaiNhanh'));
          setKhaiNhanh(null);
        },
      },
    );
  };

  const xacNhanXong = () => {
    if (!xacNhan) return;
    const { s } = xacNhan;
    const dong = () => setXacNhan(null);
    switch (xacNhan.loai) {
      case 'xoa':
      case 'xoaCaKho': {
        const drop = xacNhan.loai === 'xoaCaKho';
        xoa.mutate(
          { id: s.id, drop },
          {
            onSuccess: () => {
              toast.success(drop ? t('troLyAiKetNoi.daBoVaXoa') : t('troLyAiKetNoi.daBoGiuKho'));
              dong();
            },
          },
        );
        return;
      }
      case 'doiCheDo':
        sua.mutate(
          { id: s.id, du: { accessMode: s.accessMode === 'live' ? 'copy' : 'live' } },
          {
            onSuccess: () => {
              toast.success(t('troLyAiKetNoi.daDoiCheDo'));
              dong();
            },
          },
        );
        return;
      case 'xoaKhoCu':
        xoaKhoCu.mutate(s.id, {
          onSuccess: (kq) => {
            toast.success(t('troLyAiKetNoi.daXoaKhoCu', { ten: kq.droppedDatabase }));
            dong();
          },
        });
        return;
    }
  };

  const hop = xacNhan
    ? {
        xoa: {
          tieuDe: t('troLyAiKetNoi.xacNhanBo', { ten: xacNhan.s.name }),
          than: t('troLyAiKetNoi.xacNhanBoKhongKho'),
          nhan: t('troLyAiKetNoi.boNguon'),
          phaHuy: true,
          dangChay: xoa.isPending,
          loi: xoa.error,
        },
        // Nguồn đã có kho: hỏi đúng câu đắt — xoá kho thì không lùi được.
        xoaCaKho: {
          tieuDe: t('troLyAiKetNoi.xacNhanXoaKho', { ten: xacNhan.s.db.name ?? '' }),
          than: t('troLyAiKetNoi.xacNhanXoaKhoGhi'),
          nhan: t('troLyAiKetNoi.xoaCaKho'),
          phaHuy: true,
          dangChay: xoa.isPending,
          loi: xoa.error,
        },
        doiCheDo: {
          tieuDe: t('troLyAiKetNoi.xacNhanDoiCheDo', { ten: xacNhan.s.name }),
          than:
            xacNhan.s.accessMode === 'live'
              ? t('troLyAiKetNoi.xacNhanSangSaoChep')
              : t('troLyAiKetNoi.xacNhanSangTrucTiep'),
          nhan: t('troLyAiKetNoi.doiCheDo'),
          phaHuy: false,
          dangChay: sua.isPending,
          loi: sua.error,
        },
        xoaKhoCu: {
          tieuDe: t('troLyAiKetNoi.xacNhanXoaKhoCu', { ten: xacNhan.s.db.name ?? '' }),
          than: t('troLyAiKetNoi.xacNhanXoaKhoCuGhi'),
          nhan: t('troLyAiKetNoi.xoaKhoCu'),
          phaHuy: true,
          dangChay: xoaKhoCu.isPending,
          loi: xoaKhoCu.error,
        },
      }[xacNhan.loai]
    : null;

  // TODO(ADR-0086): đường webhook "có dữ liệu mới" phải là đường công khai (xác thực bằng `X-Notify-Secret`, không qua
  // phiên người dùng); `module.yaml` chưa khai route công khai nào cho nó — chốt cùng agent quyết đường công khai.
  const doanGoi = biMat
    ? `POST ${GOC_TRO_LY}/replica/sources/${biMat.id}/notify\nX-Notify-Secret: ${biMat.secret}\n\n{"kind":"new_data","tables":["ten_bang"]}`
    : '';

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.troLiAi') }, { label: t('nav.muc.ketNoiNguon') }]}
          titleIcon={<ApiOutlined className="text-primary-600" />}
          title={t('troLyAiKetNoi.tieuDe')}
          subtitle={t('troLyAiKetNoi.phuDe')}
          actions={
            <div className="flex flex-wrap gap-2">
              {/* Tài liệu đứng TRƯỚC các nút "Thêm": việc thật bắt đầu trước màn này — đi xin tài khoản chỉ-đọc hoặc nhờ
                  đội kỹ thuật dựng endpoint, và thứ cần gửi kèm là một tài liệu. Ba tài liệu, một nút: với người bấm
                  đây là một việc — "lấy tài liệu để gửi đi". */}
              <DropdownMenu
                menu={{
                  items: [
                    { key: 'dsn', label: t('troLyAiKetNoi.taiLieuDsn'), onClick: () => taiVe(taoDocx(HD_DSN), TEP_DSN) },
                    {
                      key: 'graphql',
                      label: t('troLyAiKetNoi.taiLieuGraphql'),
                      onClick: () => taiVe(taoDocx(HD_GRAPHQL), TEP_GRAPHQL),
                    },
                    { type: 'divider' },
                    {
                      key: 'chuanVao',
                      label: t('troLyAiKetNoi.taiLieuChuanVao'),
                      onClick: () => taiVe(taoDocx(HD_CHUAN_VAO), TEP_CHUAN_VAO),
                    },
                  ],
                }}
              >
                <Button variant="secondary" size="md" icon={<FileWordOutlined />}>
                  {t('troLyAiKetNoi.taiLieuKetNoi')} <DownOutlined className="text-xs" />
                </Button>
              </DropdownMenu>
              {q.nguonTao ? (
                <>
                  <Button variant="secondary" size="md" icon={<PlusOutlined />} onClick={() => setThemLoai('dsn')}>
                    {t('troLyAiKetNoi.themCsdl')}
                  </Button>
                  <Button variant="secondary" size="md" icon={<PlusOutlined />} onClick={() => setThemLoai('graphql')}>
                    {t('troLyAiKetNoi.themGraphqlNut')}
                  </Button>
                  <Button size="md" icon={<PlusOutlined />} onClick={() => setThemLoai('api')}>
                    {t('troLyAiKetNoi.themApiNut')}
                  </Button>
                </>
              ) : null}
            </div>
          }
        />

        <div className="rounded-lg border border-default bg-surface p-4">
          <VungDuLieu
            dangTai={nguon.isPending}
            loi={nguon.error}
            dulieu={nguon.data}
            rong={ds.length === 0}
            onThuLai={() => void nguon.refetch()}
            rongIcon={ApiOutlined}
            rongTieuDe={t('troLyAiKetNoi.rongTieuDe')}
            rongMo={t('troLyAiKetNoi.rongMoTa')}
            rongHanhDong={
              q.nguonTao ? (
                <Button icon={<PlusOutlined />} onClick={() => setThemLoai('dsn')}>
                  {t('troLyAiKetNoi.themDauTien')}
                </Button>
              ) : undefined
            }
            khungCho={
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" aria-busy="true">
                <Skeleton className="h-56 w-full" />
                <Skeleton className="h-56 w-full" />
                <Skeleton className="h-56 w-full" />
              </div>
            }
          >
            {(dsNguon) => (
              <ul aria-label={t('troLyAiKetNoi.danhSach')} className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {dsNguon.map((s) => (
                  <li key={s.id} className="min-w-0">
                    <TheNguon
                      s={s}
                      chanDoan={chanDoan[s.id]}
                      quyen={quyenThe}
                      dangKiem={kiem.isPending && kiem.variables === s.id}
                      dangDung={dung.isPending && dung.variables === s.id}
                      dangDongBo={dongBo.isPending && dongBo.variables === s.id}
                      dangKhaiNhanh={dangDoKhai === s.id}
                      dangXoaKhoCu={xoaKhoCu.isPending && xoaKhoCu.variables === s.id}
                      onKiem={() => onKiem(s.id)}
                      onDung={() => onDung(s.id)}
                      onDongBo={() => onDongBo(s.id)}
                      onDoiTuDongBo={() => doi(s.id, { autoSync: !s.autoSync })}
                      onDoiNhip={(phut) => doi(s.id, { syncIntervalSeconds: phut * 60 }, t('troLyAiKetNoi.daDoiNhip'))}
                      onDoiMoTa={(moTa) => doi(s.id, { moTa }, t('troLyAiKetNoi.daDoiMoTa'))}
                      onSoDo={() => setSoDo(s)}
                      onXoa={() => setXacNhan({ loai: s.db.name ? 'xoaCaKho' : 'xoa', s })}
                      onDoiCheDo={() => setXacNhan({ loai: 'doiCheDo', s })}
                      onSuaApi={() => setSuaApi(s)}
                      onKhaiNhanh={() => void batDauKhaiNhanh(s)}
                      onXoaKhoCu={() => setXacNhan({ loai: 'xoaKhoCu', s })}
                    />
                  </li>
                ))}
              </ul>
            )}
          </VungDuLieu>
        </div>
      </div>

      <SoDoNguon s={soDo} open={soDo !== null} onClose={() => setSoDo(null)} />
      <SuaKetNoiApi nguon={suaApi} onClose={() => setSuaApi(null)} />
      <ThemNguon
        loai={themLoai}
        onClose={() => setThemLoai(null)}
        onCreated={(s) => {
          // Bí mật webhook chỉ có nghĩa với nguồn có bản sao để báo "có dữ liệu mới" — nguồn API không đồng bộ gì.
          if (s.notifySecret && s.kind !== 'api') setBiMat({ id: s.id, secret: s.notifySecret });
        }}
        daCo={ds.map((x) => x.id)}
      />

      {hop && xacNhan ? (
        <HopXacNhan
          open
          onClose={() => setXacNhan(null)}
          tieuDe={hop.tieuDe}
          than={hop.than}
          nhanXacNhan={hop.nhan}
          phaHuy={hop.phaHuy}
          dangChay={hop.dangChay}
          loi={hop.loi}
          onXacNhan={xacNhanXong}
        />
      ) : null}

      {khaiNhanh ? (
        <Dialog
          open
          onClose={() => setKhaiNhanh(null)}
          width={760}
          title={t('troLyAiKetNoi.khaiNhanhTieuDe', { ten: khaiNhanh.s.name })}
        >
          <div className="space-y-2 pt-2">
            <p className="text-xs text-content-secondary">
              {khaiNhanh.sap ? t('troLyAiKetNoi.khaiNhanhSapGhi') : t('troLyAiKetNoi.khaiNhanhThuongGhi')}
            </p>
            {/* In NGUYÊN VĂN thứ sắp ghi, không in một dòng tóm tắt: người bấm đang nhận vào bản khai của tổ chức mình
                vài chục dòng do nền tảng soạn, và họ có quyền đọc trước. */}
            <pre className="whitespace-pre-wrap rounded-lg border border-default bg-subtle p-3 font-mono text-xs">
              {khaiNhanh.van}
            </pre>
            {/* Danh sách kiểm cho NGƯỜI, không ghi vào bản khai: mô hình không chạy được và không cần đọc nó. */}
            {khaiNhanh.sap ? (
              <details>
                <summary className="cursor-pointer text-xs font-medium text-content-secondary">
                  {t('troLyAiKetNoi.khaiNhanhKiem')}
                </summary>
                <pre className="mt-1.5 whitespace-pre-wrap rounded-lg border border-warning-500 bg-warning-50 p-3 font-mono text-xs text-warning-700">
                  {MAU_KIEM_SAP_B1}
                </pre>
              </details>
            ) : null}
            {sua.error ? (
              <p role="alert" className="rounded border border-danger-100 bg-danger-50 p-3 text-sm text-danger-700">
                {t('troLyAiKetNoi.khaiNhanhHong', { loi: moTaLoi(sua.error) })}
              </p>
            ) : null}
          </div>
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => setKhaiNhanh(null)}>
              {t('chung.huy')}
            </Button>
            <Button loading={sua.isPending} onClick={ghiKhaiNhanh}>
              {t('troLyAiKetNoi.khaiNhanhGhi')}
            </Button>
          </div>
        </Dialog>
      ) : null}

      <Dialog open={biMat !== null} onClose={() => setBiMat(null)} width={720} title={t('troLyAiKetNoi.biMatTieuDe')}>
        <div className="space-y-3 pt-2 text-sm">
          <p className="text-content-secondary">{t('troLyAiKetNoi.biMatGhi')}</p>
          <pre className="overflow-x-auto rounded-lg bg-subtle p-3 font-mono text-xs text-content-primary">{doanGoi}</pre>
          <p className="font-medium text-warning-700">{t('troLyAiKetNoi.biMatCanhBao')}</p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="outline"
              icon={<CopyOutlined />}
              onClick={() =>
                void navigator.clipboard
                  ?.writeText(doanGoi)
                  .then(() => toast.success(t('troLyAiKetNoi.daChep')))
                  .catch(() => undefined)
              }
            >
              {t('troLyAiKetNoi.chepKhoa')}
            </Button>
            <Button onClick={() => setBiMat(null)}>{t('troLyAiKetNoi.toiDaLuu')}</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
